import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Typography, Spin, Row, Col, Result } from 'antd';
import { useAuth } from '../auth/authcontext';
import { getMyBusinessUnits, BusinessUnit } from '../services/businessunitservice';
import { ShopOutlined } from '@ant-design/icons';
import UnauthorizedLayout from '../components/layouts/unauthorizedlayout';

const { Title, Text } = Typography;

const SelectBusinessUnit: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userId } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [unauthorized, setUnauthorized] = useState<boolean>(false);
  const [pendingSetup, setPendingSetup] = useState<boolean>(false);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      if (!userId) return;

      try {
        const bus = await getMyBusinessUnits(userId);
        
        if (bus.length === 0) {
          setPendingSetup(true);
        } else if (bus.length === 1 && searchParams.get('intent') !== 'switch') {
          // Auto-select and bypass only if not an explicit intent to switch
          localStorage.setItem('BusinessUnitGuid', bus[0].guid);
          localStorage.setItem('BusinessUnitName', bus[0].name);
          localStorage.setItem('BusinessUnitCode', bus[0].code || 'BU');
          navigate('/', { replace: true });
        } else {
          setBusinessUnits(bus);
        }
      } catch (error) {
        console.error("Failed to fetch business units", error);
        setUnauthorized(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessUnits();
  }, [userId, navigate]);

  const handleSelectBU = (buGuid: string, buName: string, buCode: string) => {
    localStorage.setItem('BusinessUnitGuid', buGuid);
    localStorage.setItem('BusinessUnitName', buName);
    localStorage.setItem('BusinessUnitCode', buCode || 'BU');
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f2f5' }}>
        <Spin size="large" tip="Loading your Business Units..." />
      </div>
    );
  }

  if (pendingSetup) {
    return (
      <UnauthorizedLayout>
        <Result
          status="warning"
          title="Pending Setup"
          subTitle="No Business Units assigned. Please request a portal admin to assign a BU."
        />
      </UnauthorizedLayout>
    );
  }

  if (unauthorized) {
    return (
      <UnauthorizedLayout>
        <Result
          status="403"
          title="Unauthorized"
          subTitle="You are not authorized to access this resource or an error occurred."
        />
      </UnauthorizedLayout>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: '50px 20px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Title level={2} style={{ textAlign: 'center', marginBottom: '8px' }}>Select Business Unit</Title>
        <Text style={{ display: 'block', textAlign: 'center', fontSize: '16px', marginBottom: '40px', color: '#595959' }}>
          Please select the Business Unit you would like to manage. This will determine which data you can view and edit during your session.
        </Text>
        <Row gutter={[24, 24]} justify="center">
          {businessUnits.map(bu => (
            <Col xs={24} sm={12} md={8} key={bu.guid}>
              <Card 
                hoverable 
                onClick={() => handleSelectBU(bu.guid, bu.name, bu.code)}
                style={{ textAlign: 'center', borderRadius: '8px' }}
              >
                <div style={{ 
                  width: '64px', height: '64px', borderRadius: '50%', 
                  background: 'var(--color-primary)', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '24px', fontWeight: 'bold', margin: '0 auto 16px auto'
                }}>
                  {bu.code}
                </div>
                <Title level={4}>{bu.name}</Title>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
};

export default SelectBusinessUnit;
